import React from "react";
import {
  Calendar,
  Star,
  Building2,
  Globe,
  Shield,
  Plus,
  Users,
  MapPin,
  Clock,
  Camera,
  ChevronRight,
  Home,
  Edit3,
  CheckCircle,
  Palette,
  TrendingUp,
  Lightbulb,
  FileText,
  ShieldCheck,
  MessageSquare,
  Package,
  Flame,
} from "lucide-react";
import { OverviewSkeleton } from "../UI/Skeleton";

// ─── Color Maps (explicit classes so Tailwind doesn't purge them) ─────────────
const COLOR = {
  purple: { bg: "bg-purple-50", border: "border-purple-200", text: "text-purple-600", ring: "ring-purple-100" },
  pink:   { bg: "bg-pink-50",   border: "border-pink-200",   text: "text-pink-600",   ring: "ring-pink-100"   },
  blue:   { bg: "bg-purple-50",   border: "border-purple-200",   text: "text-purple-600",   ring: "ring-purple-100"   },
  amber:  { bg: "bg-amber-50",  border: "border-amber-200",  text: "text-amber-600",  ring: "ring-amber-100"  },
  green:  { bg: "bg-green-50",  border: "border-green-200",  text: "text-green-600",  ring: "ring-green-100"  },
  indigo: { bg: "bg-purple-50", border: "border-purple-200", text: "text-purple-600", ring: "ring-purple-100" },
  red:    { bg: "bg-red-50",    border: "border-red-200",    text: "text-red-600",    ring: "ring-red-100"    },
};

// ─── Stat Card ────────────────────────────────────────────────────────────────
const StatCard = ({ label, value, icon: Icon, color, onClick }) => {
  const c = COLOR[color] || COLOR.purple;
  return (
    <button
      onClick={onClick}
      className="group relative overflow-hidden bg-white rounded-2xl border border-gray-200/70 shadow-[0_1px_3px_rgba(16,24,40,0.04)] p-5 text-left hover:shadow-lg hover:shadow-purple-900/5 hover:-translate-y-0.5 hover:border-purple-200 transition-all duration-200"
    >
      <div className="flex items-start justify-between">
        <div className="min-w-0">
          <p className="text-xs font-semibold uppercase tracking-wider text-gray-400">{label}</p>
          <p className="mt-2 font-['Manrope'] text-3xl font-extrabold tracking-tight text-gray-900 tabular-nums">{value}</p>
        </div>
        <div className={`w-11 h-11 ${c.bg} rounded-xl flex items-center justify-center flex-shrink-0`}>
          <Icon className={`w-5 h-5 ${c.text}`} />
        </div>
      </div>
      <p className="mt-3 flex items-center gap-1 text-xs font-semibold text-gray-400 group-hover:text-purple-600 transition-colors">
        View details <ChevronRight className="w-3.5 h-3.5" />
      </p>
    </button>
  );
};

// ─── Service Chip ─────────────────────────────────────────────────────────────
const ServiceChip = ({ icon: Icon, label, count, color, onClick }) => {
  const c = COLOR[color] || COLOR.purple;
  return (
    <button
      onClick={onClick}
      className="flex items-center gap-3 px-4 py-3.5 min-w-0 rounded-xl border border-gray-200/70 bg-gray-50/60 hover:bg-white hover:border-purple-200 hover:shadow-md hover:shadow-purple-900/5 transition-all duration-200"
    >
      <div className={`w-10 h-10 ${c.bg} rounded-xl flex items-center justify-center flex-shrink-0`}>
        <Icon className={`w-5 h-5 ${c.text}`} />
      </div>
      <div className="min-w-0 text-left">
        <p className="text-sm font-bold text-gray-800 truncate">{label}</p>
        <p className="text-xs text-gray-500 font-medium">{count} listed</p>
      </div>
    </button>
  );
};

// ─── Quick Action ─────────────────────────────────────────────────────────────
const QuickAction = ({ icon: Icon, label, desc, onClick }) => (
  <button
    onClick={onClick}
    className="group flex items-center gap-3.5 rounded-xl px-3 py-3 hover:bg-purple-50/70 transition-colors duration-200 text-left w-full"
  >
    <div className="w-10 h-10 rounded-xl flex-shrink-0 bg-gradient-to-br from-purple-600 to-pink-600 flex items-center justify-center shadow-md shadow-purple-600/20">
      <Icon className="w-5 h-5 text-white" />
    </div>
    <div className="min-w-0 flex-1">
      <p className="text-sm font-bold text-gray-800">{label}</p>
      <p className="text-xs text-gray-500">{desc}</p>
    </div>
    <ChevronRight className="w-4 h-4 text-gray-300 group-hover:text-purple-600 group-hover:translate-x-0.5 transition-all flex-shrink-0" />
  </button>
);

// ─── Status Tile ──────────────────────────────────────────────────────────────
const StatusTile = ({ icon: Icon, label, value, hint, ok, action, onAction }) => (
  <div className={`relative flex flex-col rounded-xl border p-4 transition-colors ${ok ? "border-gray-200/70 bg-gray-50/60" : "border-amber-200 bg-amber-50/40"}`}>
    <div className="flex items-center justify-between gap-3">
      <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${ok ? "bg-white border border-gray-200/70" : "bg-white border border-amber-200"}`}>
        <Icon className={`w-5 h-5 ${ok ? "text-purple-600" : "text-amber-500"}`} />
      </div>
      <span className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[11px] font-semibold ${ok ? "border-green-200 bg-green-50 text-green-700" : "border-amber-200 bg-white text-amber-700"}`}>
        {ok ? <CheckCircle className="w-3.5 h-3.5" /> : <Clock className="w-3.5 h-3.5" />}
        {ok ? "Done" : "Action needed"}
      </span>
    </div>
    <p className="mt-4 text-[11px] font-semibold uppercase tracking-wider text-gray-400">{label}</p>
    <p className="mt-0.5 font-['Manrope'] text-lg font-extrabold tracking-tight text-gray-900">{value}</p>
    <p className="mt-1 text-xs text-gray-500 leading-relaxed">{hint}</p>
    {!ok && action && (
      <button
        onClick={onAction}
        className="group mt-3 inline-flex items-center gap-1 self-start text-xs font-semibold text-gray-900"
      >
        {action}
        <ChevronRight className="w-3.5 h-3.5 transition-transform group-hover:translate-x-0.5" />
      </button>
    )}
  </div>
);

// ─── Venue Card ───────────────────────────────────────────────────────────────
const VenueCard = ({ venue, onNavigate }) => {
  const address = venue.serviceAddress;
  return (
    <div className="p-4 rounded-xl border border-gray-200/70 bg-gray-50/60 hover:bg-white hover:border-purple-200 hover:shadow-md hover:shadow-purple-900/5 transition-all duration-200">
      <div className="flex items-start justify-between mb-3">
        <div className="min-w-0 flex-1">
          <h4 className="text-sm font-bold text-gray-900 truncate">{venue.name}</h4>
          <p className="text-xs text-gray-500 mt-0.5 flex items-center gap-1">
            <MapPin className="w-3 h-3" />
            {address?.city}, {address?.state}
          </p>
        </div>
        <div className="flex gap-1.5 flex-shrink-0 ml-2">
          <span className={`px-2 py-0.5 rounded-md text-[10px] font-bold ${venue.isLive ? "bg-green-50 text-green-600" : "bg-gray-100 text-gray-500"}`}>
            {venue.isLive ? "Live" : "Inactive"}
          </span>
          <span className={`px-2 py-0.5 rounded-md text-[10px] font-bold ${venue.is_verified ? "bg-green-50 text-green-600" : "bg-amber-50 text-amber-600"}`}>
            {venue.is_verified ? "Verified" : "Pending"}
          </span>
        </div>
      </div>
      <button
        onClick={() => onNavigate?.("services")}
        className="flex items-center gap-1.5 text-xs font-semibold text-purple-600 hover:text-purple-700 transition-colors"
      >
        <Edit3 className="w-3 h-3" /> Manage
      </button>
    </div>
  );
};

// ─── Main Component ───────────────────────────────────────────────────────────
const DashboardOverview = ({ vendorData, services = [], onNavigate }) => {
  const vendor = vendorData?.vendor;
  const serviceCounts = vendorData?.serviceCounts;
  const bookingData = vendorData?.bookingData;
  const recentVenues = vendorData?.venueData?.recentVenues || [];

  if (!vendorData) {
    return <OverviewSkeleton />;
  }

  const stats = [
    { label: "Total Bookings",   value: bookingData?.total || 0,                  icon: Calendar, color: "purple", nav: "bookings"  },
    { label: "Pending Requests", value: bookingData?.statusWise?.pending || 0,     icon: Clock,    color: "amber",  nav: "bookings"  },
    { label: "Total Services",   value: serviceCounts?.total || 0,                 icon: Star,     color: "blue",   nav: "services"  },
    { label: "Venues",           value: serviceCounts?.venue || 0,                 icon: Home,     color: "indigo", nav: "services"  },
  ];

  const serviceTypes = [
    { icon: MapPin,    label: "Venues",       count: serviceCounts?.venue || 0,        color: "indigo" },
    { icon: Camera,    label: "Photography",  count: serviceCounts?.photography || 0,  color: "blue"   },
    { icon: Users,     label: "Caterers",     count: serviceCounts?.caterers || 0,     color: "green"  },
    { icon: Palette,  label: "Decors",       count: serviceCounts?.event_service || 0,       color: "amber"  },
    // { icon: Palette,   label: "Bridal Makeup",count: serviceCounts?.bridalMakeup || 0, color: "pink"   },
  ];

  const isApproved = vendor?.status === "approved";
  const accountStatus = [
    {
      icon: Building2,
      label: "Account",
      value: isApproved ? "Approved" : "Pending",
      hint: isApproved ? "Your business is approved on MandapAI." : "Your application is being reviewed.",
      ok: isApproved,
    },
    {
      icon: Globe,
      label: "Visibility",
      value: vendor?.isLive ? "Live" : "Hidden",
      hint: vendor?.isLive ? "Customers can see your profile." : "Your profile is hidden from customers.",
      ok: !!vendor?.isLive,
      action: "Go to Settings",
      nav: "settings",
    },
    {
      icon: Shield,
      label: "Phone",
      value: vendor?.phoneVerified ? "Verified" : "Pending",
      hint: vendor?.phoneVerified ? "Your phone number is verified." : "Your phone number is not verified yet.",
      ok: !!vendor?.phoneVerified,
    },
  ];
  const statusDone = accountStatus.filter((item) => item.ok).length;

  const quickActions = [
    { icon: Plus,       label: "Add Service",      desc: "Create a new listing",      nav: "services"  },
    { icon: Building2,  label: "Update Profile",    desc: "Edit business info",        nav: "profile"   },
    { icon: TrendingUp, label: "View Analytics",    desc: "Check performance",         nav: "analytics" },
    { icon: Lightbulb,        label: "VendorLift AI",     desc: "AI-powered tools",          nav: "ai-engine" },
  ];

  return (
    <div className="space-y-5 lg:space-y-6">
      {/* Welcome */}
      <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-purple-600 to-pink-600 p-6 sm:p-8 text-white shadow-xl shadow-purple-600/20">
        <div className="pointer-events-none absolute -right-16 -top-24 h-72 w-72 rounded-full bg-white/10 blur-2xl" />
        <div className="pointer-events-none absolute -bottom-28 left-1/3 h-72 w-72 rounded-full bg-white/10 blur-2xl" />
        <div className="relative flex flex-col sm:flex-row sm:items-center sm:justify-between gap-5">
          <div className="min-w-0">
            <span className="inline-flex items-center gap-1.5 rounded-full bg-white/15 px-3 py-1 text-xs font-semibold backdrop-blur-sm">
              <span className="h-1.5 w-1.5 rounded-full bg-white" />
              {vendor?.status?.charAt(0).toUpperCase() + vendor?.status?.slice(1)}
            </span>
            <h1 className="mt-3 font-['Manrope'] text-2xl sm:text-3xl font-extrabold tracking-tight">
              Welcome back, {vendor?.contactDetails?.name?.split(" ")[0] || "there"}!
            </h1>
            <p className="text-white/80 text-sm mt-1.5 truncate">
              {vendor?.business_name} {vendor?.contactDetails?.phone ? `· ${vendor.contactDetails.phone}` : ""}
            </p>
          </div>
          <div className="flex flex-wrap gap-2.5 flex-shrink-0">
            <button
              onClick={() => onNavigate?.("services")}
              className="inline-flex items-center gap-2 rounded-xl bg-white px-4 py-2.5 text-sm font-semibold text-purple-700 shadow-lg shadow-purple-900/10 hover:bg-purple-50 transition-colors"
            >
              <Plus className="w-4 h-4" /> Add Service
            </button>
            <button
              onClick={() => onNavigate?.("bookings")}
              className="inline-flex items-center gap-2 rounded-xl bg-white/15 px-4 py-2.5 text-sm font-semibold text-white backdrop-blur-sm hover:bg-white/25 transition-colors"
            >
              <Calendar className="w-4 h-4" /> View Bookings
            </button>
          </div>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {stats.map((s) => (
          <StatCard key={s.label} {...s} onClick={() => onNavigate?.(s.nav)} />
        ))}
      </div>

      {/* Account Status */}
      <div className="bg-white rounded-2xl border border-gray-200/70 shadow-[0_1px_3px_rgba(16,24,40,0.04)] p-5 sm:p-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-purple-600 to-pink-600 flex items-center justify-center shadow-md shadow-purple-600/20 flex-shrink-0">
              <ShieldCheck className="w-5 h-5 text-white" />
            </div>
            <div>
              <h2 className="font-['Manrope'] text-base font-bold text-gray-900">Account status</h2>
              <p className="text-xs text-gray-500">
                {statusDone === accountStatus.length
                  ? "Everything is set — customers can find and book you."
                  : "Finish the remaining steps so customers can find and book you."}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3 sm:w-56">
            <div className="h-2 flex-1 rounded-full bg-gray-100 overflow-hidden">
              <div
                className="h-full rounded-full bg-gradient-to-r from-purple-600 to-pink-600 transition-all duration-700"
                style={{ width: `${(statusDone / accountStatus.length) * 100}%` }}
              />
            </div>
            <span className="text-xs font-bold text-gray-900 tabular-nums whitespace-nowrap">
              {statusDone} of {accountStatus.length}
            </span>
          </div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          {accountStatus.map((item) => (
            <StatusTile key={item.label} {...item} onAction={() => onNavigate?.(item.nav)} />
          ))}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5 lg:gap-6">
        {/* Left — Services + Venues */}
        <div className="lg:col-span-2 space-y-5">
          {/* Services */}
          <div className="bg-white rounded-2xl border border-gray-200/70 shadow-[0_1px_3px_rgba(16,24,40,0.04)] p-5 sm:p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-['Manrope'] text-base font-bold text-gray-900">Your Services</h2>
              <button
                onClick={() => onNavigate?.("services")}
                className="text-xs font-semibold text-purple-600 hover:text-purple-700 flex items-center gap-1 transition-colors"
              >
                View all <ChevronRight className="w-3.5 h-3.5" />
              </button>
            </div>
            <div className="grid grid-cols-1 min-[420px]:grid-cols-2 xl:grid-cols-4 gap-3">
              {serviceTypes.map((s) => (
                <ServiceChip key={s.label} {...s} onClick={() => onNavigate?.("services")} />
              ))}
            </div>
          </div>

          {/* Venues */}
          {recentVenues.length > 0 && (
            <div className="bg-white rounded-2xl border border-gray-200/70 shadow-[0_1px_3px_rgba(16,24,40,0.04)] p-5 sm:p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="font-['Manrope'] text-base font-bold text-gray-900">Recent Venues</h2>
                <span className="text-xs font-bold text-gray-400">{recentVenues.length} total</span>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {recentVenues.slice(0, 4).map((venue) => (
                  <VenueCard key={venue._id} venue={venue} onNavigate={onNavigate} />
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Right — Quick Actions */}
        <div className="space-y-5">
          <div className="bg-white rounded-2xl border border-gray-200/70 shadow-[0_1px_3px_rgba(16,24,40,0.04)] p-5 sm:p-6">
            <h2 className="font-['Manrope'] text-base font-bold text-gray-900 mb-3">Quick Actions</h2>
            <div className="space-y-1 -mx-1">
              {quickActions.map((a) => (
                <QuickAction key={a.label} {...a} onClick={() => onNavigate?.(a.nav)} />
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Recent Bookings Summary */}
      <div className="bg-white rounded-2xl border border-gray-200/70 shadow-[0_1px_3px_rgba(16,24,40,0.04)] p-5 sm:p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-['Manrope'] text-base font-bold text-gray-900">Bookings</h2>
          <button
            onClick={() => onNavigate?.("bookings")}
            className="text-xs font-semibold text-purple-600 hover:text-purple-700 flex items-center gap-1 transition-colors"
          >
            View all <ChevronRight className="w-3.5 h-3.5" />
          </button>
        </div>
        {bookingData?.recentBookings?.length > 0 ? (
          <div className="-mx-5 sm:-mx-6 overflow-x-auto">
            <table className="w-full min-w-[520px] text-sm">
              <thead className="border-y border-gray-100 bg-gray-50/80">
                <tr>
                  {["Ref", "Event date", "Slot", "Status"].map((h) => (
                    <th key={h} className="px-5 sm:px-6 py-2.5 text-left text-[11px] font-semibold uppercase tracking-wider text-gray-500 last:text-right">
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {bookingData.recentBookings.slice(0, 3).map((b) => (
                  <tr
                    key={b.id}
                    onClick={() => onNavigate?.("bookings")}
                    className="cursor-pointer hover:bg-purple-50/40 transition-colors"
                  >
                    <td className="px-5 sm:px-6 py-3 font-mono text-xs font-semibold text-gray-700">#{b.booking_reference}</td>
                    <td className="px-5 sm:px-6 py-3 text-gray-900">{new Date(b.event_date).toLocaleDateString("en-IN")}</td>
                    <td className="px-5 sm:px-6 py-3 capitalize text-gray-600">{b.slot}</td>
                    <td className="px-5 sm:px-6 py-3 text-right">
                      <span className={`px-2.5 py-1 rounded-full text-[11px] font-bold capitalize ${
                        b.booking_status === "confirmed" ? "bg-green-50 text-green-600"
                          : b.booking_status === "pending" ? "bg-amber-50 text-amber-600"
                          : "bg-gray-100 text-gray-500"
                      }`}>
                        {b.booking_status}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="text-center py-8">
            <Calendar className="w-8 h-8 text-gray-200 mx-auto mb-2" />
            <p className="text-xs text-gray-400 font-medium">No bookings yet</p>
          </div>
        )}
      </div>

      {/* VendorLift AI Tools */}
      <div className="bg-white rounded-2xl border border-gray-200/70 shadow-[0_1px_3px_rgba(16,24,40,0.04)] p-5 sm:p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2.5">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-purple-600 to-pink-600 flex items-center justify-center shadow-md shadow-purple-600/20">
              <Lightbulb className="w-5 h-5 text-white" />
            </div>
            <div>
              <h2 className="font-['Manrope'] text-base font-bold text-gray-900">VendorLift</h2>
              <p className="text-xs text-gray-500">Your growth toolkit</p>
            </div>
          </div>
          <button
            onClick={() => onNavigate?.("ai-engine")}
            className="text-xs font-semibold text-purple-600 hover:text-purple-700 flex items-center gap-1 transition-colors"
          >
            Open <ChevronRight className="w-3.5 h-3.5" />
          </button>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-6 gap-3">
          {[
            { icon: Flame,         label: "Lead Scoring",     desc: "Rank hot leads first",       gradient: "from-pink-500 to-pink-500" },
            { icon: Package,       label: "Package Builder",  desc: "Auto-generate pricing",      gradient: "from-purple-500 to-pink-500" },
            { icon: MessageSquare, label: "Auto-Reply",       desc: "Reply when you're offline",  gradient: "from-purple-500 to-pink-500" },
            { icon: Calendar,      label: "Calendar",         desc: "Block dates instantly",       gradient: "from-purple-500 to-pink-500" },
            { icon: FileText,      label: "Doc Reader",       desc: "Extract GSTIN & PAN",        gradient: "from-purple-500 to-pink-500" },
            { icon: ShieldCheck,   label: "Verification",     desc: "Verify bank & GSTIN",        gradient: "from-purple-500 to-pink-500" },
          ].map((f) => (
            <button
              key={f.label}
              onClick={() => onNavigate?.("ai-engine")}
              className="group text-left p-4 hover:-translate-y-0.5 rounded-xl border border-gray-200/70 bg-gray-50/60 hover:bg-white hover:border-purple-200 hover:shadow-md hover:shadow-purple-900/5 transition-all duration-200"
            >
              <div className="w-9 h-9 rounded-xl mb-3 group-hover:scale-105 transition-transform bg-gradient-to-br from-purple-600 to-pink-600 flex items-center justify-center shadow-md shadow-purple-600/20">
                <f.icon className="w-4 h-4 text-white" />
              </div>
              <p className="text-sm font-bold text-gray-800 leading-tight">{f.label}</p>
              <p className="text-xs text-gray-500 mt-1 leading-snug">{f.desc}</p>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

export default DashboardOverview;
